// useSignupFlow — the orchestrator. Replaces the monolith's top-level state +
// step runners + handlers (OnboardingChatbotV1.vue). Composes the chat timeline,
// tracking, and API composables; owns the answer state, the derived progress/path
// computeds, the per-step bot-message sequences, the signup sub-phase machine, and
// editable back-navigation. The pure branching lives in config/flow-graph; the
// wording in config/wordings + success-wordings.
//
// localStorage resume: intentionally NOT implemented in M1. The prototype calls
// store.reset() on mount (which wipes its NT_ONBOARDING_V1 key), so client-side
// resume is dead in V1; real persistence belongs to the backend-session milestone.
import { type Ref } from 'vue'
import { nextStep, type StepKey } from '../config/flow-graph'
import {
  UNIVERSE_OPTIONS,
  MARKETPLACE_ROLE_OPTIONS,
  MN_ROLE_OPTIONS,
  CARE_TYPE_OPTIONS,
  SUBPROFILE_OPTIONS,
  UNIVERSE_BUBBLE_LABELS,
  ROLE_BUBBLE_LABELS,
  SUBPROFILE_BUBBLE_LABELS,
  type ChoiceOption,
} from '../config/options'
import {
  UNIVERSE_INTRO,
  MP_ROLE_INTRO,
  MP_CARETYPE_INTRO,
  MN_SUBPROFILE_INTRO,
  addressIntro,
  addressCtaLabel,
  mnRoleIntro,
  longformIntro,
  availabilityBubble,
  SIGNUP_FAILED,
} from '../config/wordings'
import { resolveCharacterKey } from '../config/characters'
import { useChatTimeline } from './useChatTimeline'
import { useSignupTracking } from './useSignupTracking'
import { useSignupApi } from './useSignupApi'
import { useSignupState } from './useSignupState'
import { useSignupAuth } from './useSignupAuth'
import { geoToRegistrationPayload, type AddressGeo } from './useAddressAutocomplete'

const SUCCESS_REVEAL_PAUSE_MS = 500
const SUCCESS_TRACKING_GRACE_MS = 2000

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

export function useSignupFlow() {
  const timeline = useChatTimeline()
  const tracking = useSignupTracking()
  const api = useSignupApi()

  // Reactive answer state + derived progress/path computeds live in useSignupState;
  // the orchestrator drives all transitions over them.
  const state = useSignupState()
  const {
    universe,
    firstInterest,
    role,
    subProfile,
    adresse,
    villeLabel,
    addressGeo,
    typeGarde,
    formData,
    authMethod,
    cguAccepted,
    accountRedirectUrl,
    characterKey,
    stepKey,
    phase,
    signupSubPhase,
    submitInFlight,
    availabilityCount,
    emailError,
    activeChar,
    pathKey,
    currentPath,
    stepIdx,
    totalSteps,
    totalKnown,
    progressPct,
    showProgress,
    stepAnnouncement,
    signupIntroBubble,
    successConfig,
    snapshot,
  } = state

  // ── Transient orchestrator state (imperative, not reactive answer state) ──
  let signupCompleted = false // ultimate once-only guard for finishSignup
  // index in the timeline where each step's content begins (for back-nav rewind)
  const stepStartIdx: Record<string, number> = {}

  // Signup sub-phase machine (auth-choice → email/google → optin) lives in useSignupAuth;
  // it shares this timeline/state and calls finishSignup (hoisted below) on completion.
  const {
    chooseGoogle,
    chooseEmail,
    submitEmail,
    alreadyHaveAccount,
    acceptCgua,
  } = useSignupAuth({ state, timeline, api, tracking, stepStartIdx, finishSignup })

  // ── Step runners ──────────────────────────────────────────────
  // `instant` (set on back-navigation) skips the typing animation so the card
  // reappears immediately.
  async function runStep(key: StepKey, instant = false): Promise<void> {
    const gen = timeline.bumpGen()
    const ok = () => timeline.currentGen() === gen
    stepStartIdx[key] = timeline.messages.value.length

    if (key === 'universe') {
      await timeline.chainMessages([...UNIVERSE_INTRO], { gen, quiet: instant })
      if (!ok()) return
      timeline.addCard('choice', {
        stepKey: 'universe',
        options: UNIVERSE_OPTIONS,
        selectedKey: universe.value,
      })
      return
    }
    if (key === 'mp-role') {
      await timeline.chainMessages([MP_ROLE_INTRO], { gen, quiet: instant })
      if (!ok()) return
      timeline.addCard('choice', {
        stepKey: 'mp-role',
        options: MARKETPLACE_ROLE_OPTIONS,
        selectedKey: role.value,
      })
      return
    }
    if (key === 'mn-role') {
      await timeline.chainMessages([mnRoleIntro(universe.value)], { gen, quiet: instant })
      if (!ok()) return
      timeline.addCard('choice', {
        stepKey: 'mn-role',
        options: MN_ROLE_OPTIONS,
        selectedKey: role.value,
      })
      return
    }
    if (key === 'mp-zone' || key === 'mam-zone') {
      await timeline.chainMessages([addressIntro(key)], { gen, quiet: instant })
      if (!ok()) return
      timeline.addCard('address', {
        stepKey: key,
        ctaLabel: addressCtaLabel(key),
        initialLabel: adresse.value || null,
        initialCity: villeLabel.value || null,
        initialGeo: addressGeo.value,
      })
      return
    }
    if (key === 'mp-caretype') {
      await timeline.chainMessages([MP_CARETYPE_INTRO], { gen, quiet: instant })
      if (!ok()) return
      timeline.addCard('caretype', { selectedKey: typeGarde.value })
      return
    }
    if (key === 'mn-subprofile') {
      await timeline.chainMessages([MN_SUBPROFILE_INTRO], { gen, quiet: instant })
      if (!ok()) return
      timeline.addCard('choice', {
        stepKey: 'mn-subprofile',
        options: SUBPROFILE_OPTIONS,
        selectedKey: subProfile.value,
      })
      return
    }
    if (key === 'mn-longform') {
      await timeline.chainMessages([longformIntro(subProfile.value)], { gen, quiet: instant })
      if (!ok()) return
      timeline.addCard('longform', { formType: subProfile.value })
      return
    }
    if (key === 'signup') {
      // Fresh acceptance: re-entering this step (forward or via back-navigation) must
      // start with consent un-given until the user taps `acceptCgua` again.
      cguAccepted.value = false
      signupSubPhase.value = 'intro'
      await timeline.chainMessages([signupIntroBubble.value], { gen, quiet: instant })
      if (!ok()) return
      signupSubPhase.value = 'consent'
      timeline.addCard('cgua-consent')
      return
    }
  }

  function advance(): void {
    const next = nextStep(stepKey.value, currentPath.value)
    if (next) {
      stepKey.value = next
      void runStep(next)
    }
  }

  // ── Selection handlers ────────────────────────────────────────
  function selectUniverse(key: string): void {
    if (stepKey.value !== 'universe') return
    universe.value = key
    firstInterest.value = key
    const opt = UNIVERSE_OPTIONS.find((o: ChoiceOption) => o.key === key)
    timeline.addUser(UNIVERSE_BUBBLE_LABELS[key] || key, { step: 'universe', icon: opt?.icon })
    timeline.removeFirstOfKind('choice')
    void tracking.trackStep('finished-signup-step-universe-choice', snapshot())
    stepKey.value = key === 'marketplace' ? 'mp-role' : 'mn-role'
    timeline.schedule(() => void runStep(stepKey.value))
  }

  function selectRole(key: string): void {
    if (stepKey.value !== 'mp-role' && stepKey.value !== 'mn-role') return
    role.value = key
    timeline.addUser(ROLE_BUBBLE_LABELS[key] || key, { step: stepKey.value })
    timeline.removeFirstOfKind('choice')
    void tracking.trackStep('finished-signup-step-listing-type', snapshot())
    timeline.schedule(() => advance())
  }

  function selectSubProfile(key: string): void {
    if (stepKey.value !== 'mn-subprofile') return
    subProfile.value = key
    timeline.addUser(SUBPROFILE_BUBBLE_LABELS[key] || key, { step: 'mn-subprofile' })
    timeline.removeFirstOfKind('choice')
    void tracking.trackStep('finished-signup-step-organisation-type', snapshot())
    timeline.schedule(() => advance())
  }

  function selectCareType(key: string): void {
    if (stepKey.value !== 'mp-caretype') return
    typeGarde.value = key
    const opt = CARE_TYPE_OPTIONS.find((o) => o.key === key)
    timeline.addUser(`${opt?.emoji || ''} ${opt?.label || key}`, { step: 'mp-caretype' })
    timeline.removeFirstOfKind('caretype')
    void tracking.trackStep('finished-signup-step-care-type', snapshot())
    timeline.schedule(() => advance())
  }

  async function confirmAddress(address: {
    label: string
    city: string
    latitude?: number | null
    longitude?: number | null
    geo: AddressGeo
  }): Promise<void> {
    if (stepKey.value !== 'mp-zone' && stepKey.value !== 'mam-zone') return
    const wasMam = stepKey.value === 'mam-zone'
    // A back-navigation during the awaits below bumps the generation: a late availability
    // response must neither append its bubble to the rewound timeline nor advance the step.
    const gen = timeline.currentGen()
    adresse.value = address.label
    villeLabel.value = address.city || address.label
    addressGeo.value = address.geo
    timeline.addUser(`📍 ${address.city || address.label}`, { step: stepKey.value })
    timeline.removeFirstOfKind('address')
    void tracking.trackStep('finished-signup-step-address', snapshot())
    if (!wasMam) {
      if (availabilityCount.value === null) {
        // Live geo count from the marketplace search when the picked address has coordinates.
        let count = 0
        if (address.latitude && address.longitude) {
          const r = await api.fetchAvailability({
            role: role.value || '',
            geoLatitude: address.latitude,
            geoLongitude: address.longitude,
          })
          if (timeline.isAborted(gen)) return
          if (r.ok && r.data) count = r.data.count
        }
        availabilityCount.value = count
      }
      // Only show the social-proof bubble when there are ads nearby — on a genuine 0 (or a failed
      // call) even the count-free wording would claim profiles that don't exist, so skip it.
      if (availabilityCount.value > 0) {
        await timeline.chainMessages(
          [availabilityBubble({ role: role.value, count: availabilityCount.value })],
          { gen },
        )
        if (timeline.isAborted(gen)) return
      }
    }
    advance()
  }

  // ── Long-form (org sub-profiles) terminal ─────────────────────
  async function onLongFormSubmit(payload: Record<string, unknown>): Promise<void> {
    if (submitInFlight.value) return
    submitInFlight.value = true
    formData.value = { ...payload, subProfile: subProfile.value }
    cguAccepted.value = !!payload.cguAccepted
    authMethod.value = 'email'
    // Create the org account BEFORE closing the long-form card, so a FAILED creation surfaces an
    // error and leaves the card for a retry instead of showing the success screen for a signup
    // that never happened (same guard as the email path's submitEmail). The disabled submit
    // button (submitInFlight) is the in-flight feedback; the user bubble is only added on success.
    // Key-map the org long-form to the /registration contract (chatbot vocab; backend translates
    // the sub-profile role + builds the org account). Org roles don't require a password
    // (RegistrationForm gates that on person roles only).
    const result = await api.createOrganisationAccount({
      universe: universe.value,
      role: subProfile.value,
      email: payload.email,
      first_name: payload.prenom,
      last_name: payload.nom,
      // Org-name field key differs by sub-profile: mam/rpe/trainer collect `nomStructure`,
      // agency collects `raisonSociale`. The backend requires organisation_name for every
      // non-person role, so forward whichever the form supplied.
      organisation_name: payload.nomStructure ?? payload.raisonSociale,
      // mam-only free-text "Présentation de la MAM" → User#mam_presentation.
      mam_presentation: payload.presentation,
      gender: payload.gender,
      // rpe/trainer collect their address inline as ville/codePostal (no global address
      // step); the backend requires city/zip for the ram/formation roles.
      city: payload.ville,
      zip: payload.codePostal,
      terms_of_use: cguAccepted.value,
      ...geoToRegistrationPayload(addressGeo.value),
    })
    if (!result.ok) {
      await timeline.chainMessages([SIGNUP_FAILED])
      submitInFlight.value = false
      return // long-form card stays on screen → the user can retry
    }
    accountRedirectUrl.value = result.data?.redirectUrl ?? null
    const displayName =
      ((payload.nomStructure ?? payload.raisonSociale) as string) ||
      `${(payload.prenom as string) || ''} ${(payload.nom as string) || ''}`.trim() ||
      (payload.email as string)
    timeline.addUser(`✅ ${displayName}`)
    timeline.removeFirstOfKind('longform')
    await finishSignup()
  }

  // ── Completion ────────────────────────────────────────────────
  // `skipTracking` lets the Google OAuth success path reuse the exact success-screen
  // swap WITHOUT re-emitting `signup` client-side: the backend already fired the event
  // server-side (with funnel_variant) for that flow — see onOAuthSuccess in useSignupAuth.
  // The email path leaves it false so it still emits once, client-side.
  //
  // The success CTA navigates away, which cancels any request still in flight, so the
  // success events must be through before the screen shows: wait for them, bounded by a
  // grace period so a slow network never holds the screen.
  async function finishSignup({ skipTracking = false } = {}): Promise<void> {
    if (signupCompleted) return
    signupCompleted = true
    const tracked = skipTracking ? Promise.resolve() : tracking.trackSignupSuccess(snapshot())
    await Promise.all([sleep(SUCCESS_REVEAL_PAUSE_MS), Promise.race([tracked, sleep(SUCCESS_TRACKING_GRACE_MS)])])
    phase.value = 'success'
  }

  // ── Editable back-navigation ──────────────────────────────────
  // Click an editable user bubble → rewind to that step's start, then re-run it
  // in instant mode. The `cgua` case re-shows the consent card (clearing acceptance);
  // the `signup-method` case re-opens the auth-choice card.
  async function goBackToStep(step: string): Promise<void> {
    const startIdx = stepStartIdx[step]
    if (startIdx === undefined) return
    timeline.bumpGen()
    timeline.clearPending()
    timeline.truncateFrom(startIdx)
    submitInFlight.value = false
    signupSubPhase.value = 'intro'

    if (step === 'cgua') {
      cguAccepted.value = false
      signupSubPhase.value = 'consent'
      timeline.addCard('cgua-consent')
      return
    }
    if (step === 'signup-method') {
      authMethod.value = null
      signupSubPhase.value = 'method-choice'
      timeline.addCard('auth-choice')
      return
    }
    stepKey.value = step as StepKey
    await runStep(step as StepKey, true)
  }

  // ── Lifecycle ─────────────────────────────────────────────────
  function reset(): void {
    universe.value = null
    firstInterest.value = null
    role.value = null
    subProfile.value = null
    adresse.value = ''
    villeLabel.value = ''
    addressGeo.value = null
    typeGarde.value = null
    formData.value = {}
    authMethod.value = null
    cguAccepted.value = false
    accountRedirectUrl.value = null
    availabilityCount.value = null
    emailError.value = null
    signupCompleted = false
    submitInFlight.value = false
    phase.value = 'chat'
    signupSubPhase.value = 'intro'
    stepKey.value = 'universe'
    timeline.reset()
  }

  // Restore this session's character (or pick + persist one), fire the arrival tracking,
  // and start at the universe step. Persisting keeps the same face/name when the chat is
  // reopened within the session. `rng` is injectable for deterministic tests.
  function start(rng: () => number = Math.random): void {
    reset()
    characterKey.value = resolveCharacterKey(rng)
    void tracking.identify()
    void tracking.trackStep('saw-registration-page', snapshot())
    void runStep('universe')
  }

  // Success-screen redirect handlers. The backend's post-signup redirect (captured at
  // account creation — same landing as a classic web signup) is the source of truth;
  // the config's primaryDest is a static, known-real fallback for the degraded case
  // where the backend couldn't compute one (redirectUrl: null).
  function onSuccessPrimary(): void {
    const dest = accountRedirectUrl.value ?? successConfig.value.primaryDest
    if (typeof window !== 'undefined') window.location.href = dest
  }
  function onSuccessSecondary(): void {
    const sec = successConfig.value.secondary
    if (sec && typeof window !== 'undefined') window.location.href = sec.dest
  }

  return {
    // timeline (messages + helpers) spread for the transcript renderer
    messages: timeline.messages as Ref<ReturnType<typeof useChatTimeline>['messages']['value']>,
    // state
    universe,
    firstInterest,
    role,
    subProfile,
    adresse,
    villeLabel,
    addressGeo,
    typeGarde,
    formData,
    authMethod,
    cguAccepted,
    characterKey,
    stepKey,
    phase,
    signupSubPhase,
    submitInFlight,
    availabilityCount,
    emailError,
    // derived
    activeChar,
    pathKey,
    currentPath,
    stepIdx,
    totalSteps,
    totalKnown,
    progressPct,
    showProgress,
    stepAnnouncement,
    signupIntroBubble,
    successConfig,
    // handlers
    start,
    reset,
    runStep,
    advance,
    goBackToStep,
    selectUniverse,
    selectRole,
    selectSubProfile,
    selectCareType,
    confirmAddress,
    onLongFormSubmit,
    chooseGoogle,
    chooseEmail,
    submitEmail,
    alreadyHaveAccount,
    acceptCgua,
    finishSignup,
    onSuccessPrimary,
    onSuccessSecondary,
  }
}
